import { DateRange } from "react-day-picker";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

interface PaginationOptions {
  page: number;
  itemsPerPage: number;
}

interface SearchOptions {
  searchTerm?: string;
  paymentMethod?: string;
  bank?: string;
}

export const usePaymentsData = (
  dateRange?: DateRange,
  pagination?: PaginationOptions,
  search?: SearchOptions
) => {
  const hasDateFilter = !!dateRange?.from;

  // Query to fetch payments with pagination using database function
  const { data: result, isLoading } = useQuery({
    queryKey: [
      "payments-summary",
      dateRange?.from?.toISOString(),
      dateRange?.to?.toISOString(),
      pagination?.page,
      pagination?.itemsPerPage,
      search?.searchTerm,
      search?.paymentMethod,
      search?.bank,
    ],
    queryFn: async () => {
      // Prepare parameters for the database function
      let dateFrom: string | null = null;
      let dateTo: string | null = null;

      if (dateRange?.from) {
        const year = dateRange.from.getFullYear();
        const month = String(dateRange.from.getMonth() + 1).padStart(2, "0");
        const day = String(dateRange.from.getDate()).padStart(2, "0");
        dateFrom = `${year}-${month}-${day}`;
      }

      if (dateRange?.to) {
        // Fix timezone issue: extract date before timezone conversion
        const year = dateRange.to.getFullYear();
        const month = String(dateRange.to.getMonth() + 1).padStart(2, "0");
        const day = String(dateRange.to.getDate()).padStart(2, "0");
        dateTo = `${year}-${month}-${day}`;
      }

      const searchTerm = search?.searchTerm?.trim() || null;
      const paymentMethodFilter = search?.paymentMethod || null;
      const bankFilter = search?.bank || null;

      // Calculate pagination parameters
      const page = pagination?.page || 1;
      const itemsPerPage =
        pagination?.itemsPerPage || (hasDateFilter ? 5000 : 100);
      const offset = (page - 1) * itemsPerPage;

      const { data, error } = await supabase.rpc("search_payments", {
        date_from: dateFrom,
        date_to: dateTo,
        search_term: searchTerm,
        payment_method_filter: paymentMethodFilter,
        bank_filter: bankFilter,
        limit_count: itemsPerPage,
        offset_count: offset,
      });

      if (error) {
        console.error("Error fetching payments:", error);
        throw new Error("Failed to fetch payments");
      }

      // Transform the data to match the expected format
      const payments =
        data?.map((payment: any) => ({
          id: payment.id,
          amount: payment.amount,
          reference: payment.reference,
          image_url: payment.image_url,
          created_at: payment.created_at,
          verified_at: payment.verified_at,
          is_verified: payment.is_verified,
          created_by: payment.created_by,
          payment_method: payment.payment_method,
          sales_request: {
            id: payment.sales_request_id,
            ...payment.sales_request,
          },
          client: {
            name: payment.client_name,
            tax_id: payment.client_tax_id,
          },
          manual_factura: payment.manual_factura,
        })) || [];

      const totalCount = data?.length > 0 ? data[0].total_count : 0;

      return {
        payments,
        totalCount,
      };
    },
  });

  return {
    payments: result?.payments || [],
    totalCount: result?.totalCount || 0,
    isLoading,
    hasDateFilter,
  };
};
